import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { sensorsApi } from '../services/api';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  SignalIcon,
  SignalSlashIcon,
  WrenchIcon,
  ExclamationTriangleIcon,
  MapPinIcon,
  ClockIcon,
  ArrowUpTrayIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { SkeletonSensorCard } from '../components/common/Skeleton';
import EmptyState from '../components/common/EmptyState';
import RegisterSensorModal from '../components/sensors/RegisterSensorModal';
import CsvImportModal from '../components/sensors/CsvImportModal';
import DedroneIntegrationsList from '../components/sensors/DedroneIntegrationsList';

type SensorStatus = 'ONLINE' | 'OFFLINE' | 'MAINTENANCE' | 'ERROR';

const statusConfig: Record<SensorStatus, { icon: any; color: string; bgColor: string; label: string }> = {
  ONLINE: { icon: SignalIcon, color: 'text-accent', bgColor: 'bg-accent/10', label: 'Online' },
  OFFLINE: { icon: SignalSlashIcon, color: 'text-gray-400', bgColor: 'bg-gray-500/10', label: 'Offline' },
  MAINTENANCE: { icon: WrenchIcon, color: 'text-yellow-400', bgColor: 'bg-yellow-500/10', label: 'Maintenance' },
  ERROR: { icon: ExclamationTriangleIcon, color: 'text-alert', bgColor: 'bg-alert/10', label: 'Error' },
};

// Display labels for sensor types. The registration flow only exposes three
// "models" (DeDrone / Dronetag / Generic Remote ID) but legacy sensor rows
// may carry any of the underlying enum values, so keep the full mapping
// for the cards rendered below.
const sensorTypeLabels: Record<string, string> = {
  RADAR: 'Radar',
  RF_DETECTOR: 'RF Detector',
  OPTICAL: 'Optical',
  ADS_B: 'ADS-B',
  ACOUSTIC: 'Acoustic',
  FUSION: 'Multi-Sensor Fusion',
  MULTI_SENSOR: 'Multi-Sensor Fusion',
  MANUAL: 'Manual Entry',
};

// Render a more specific label when the sensor record encodes its model
// via the dataFormat field. The three current models map to:
//   - DUMP        → "Dronetag"
//   - JSON_CUSTOM → "Generic Remote ID" (when registered via the new modal)
//   - everything else → the sensor type label
function sensorModelLabel(sensor: any): string {
  if (sensor.dataFormat === 'DUMP') return 'Dronetag';
  // Dedrone-discovered sub-sensors come through with MULTI_SENSOR /
  // sometimes have a Dedrone-prefixed name; if the configuration carries
  // a dedrone marker, surface that.
  if (sensor.configuration?.source === 'dedrone' || sensor.metadata?.source === 'dedrone') {
    return 'DeDrone';
  }
  return sensorTypeLabels[sensor.sensorType] || sensor.sensorType;
}

export default function SensorsPage() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<SensorStatus | 'ALL'>('ALL');
  const [showRegister, setShowRegister] = useState(false);
  const [showCsvImport, setShowCsvImport] = useState(false);

  // The API caps pageSize at 100. The page itself has no pagination UI,
  // so request the max — anything more than 100 sensors per tenant is a
  // signal we need to wire in proper pagination, not a one-off pageSize
  // bump. Without this the list defaulted to 25, which silently hid
  // sensors when (e.g.) a Dedrone integration auto-discovered 30+ rows.
  const { data: sensorsData, isLoading } = useQuery({
    queryKey: ['sensors'],
    queryFn: () => sensorsApi.list({ pageSize: 100 }),
  });

  const sensors = sensorsData?.data || [];
  const filteredSensors = sensors
    .filter((s: any) => statusFilter === 'ALL' ? true : s.status === statusFilter)
    .filter((s: any) => s.name.toLowerCase().includes(searchTerm.toLowerCase()));

  const getTimeSinceLastSeen = (lastSeen: string) => {
    const diff = Math.floor((Date.now() - new Date(lastSeen).getTime()) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  return (
    <div className="min-h-screen bg-navy-900 p-6 grid-overlay">
      <div className="mb-6">
        <h1 className="page-title">Sensors</h1>
        <p className="mt-1 text-sm text-gray-500">Monitor and manage detection sensors</p>
      </div>

      <div className="mb-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex gap-3 flex-1 w-full sm:w-auto">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
            <input type="text" placeholder="Search sensors..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-navy-800 border border-navy-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent" />
          </div>
          <div className="flex gap-1 bg-navy-800 rounded-lg p-1 border border-navy-700">
            <button onClick={() => setStatusFilter('ALL')} className={clsx('px-3 py-1.5 rounded-md text-xs font-medium transition-all', statusFilter === 'ALL' ? 'bg-accent text-navy-900' : 'text-gray-400 hover:text-white')}>All</button>
            {(Object.keys(statusConfig) as SensorStatus[]).map((status) => (
              <button key={status} onClick={() => setStatusFilter(status)} className={clsx('px-3 py-1.5 rounded-md text-xs font-medium transition-all', statusFilter === status ? 'bg-accent text-navy-900' : 'text-gray-400 hover:text-white')}>
                {statusConfig[status].label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex gap-3">
          <button onClick={() => setShowCsvImport(true)} className="btn-secondary flex items-center gap-2"><ArrowUpTrayIcon className="h-4 w-4" />Import CSV</button>
          <button onClick={() => setShowRegister(true)} className="btn-primary flex items-center gap-2"><PlusIcon className="h-4 w-4" />Register Sensor</button>
        </div>
      </div>

      {/* Dedrone integration list — appears above the sensor cards when the
          tenant has any DroneTracker servers connected. Hides itself when
          there are none. Phase 10 moved this surface here from Settings so
          a single "Sensors" page covers every model. */}
      <div className="mb-6">
        <DedroneIntegrationsList />
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => <SkeletonSensorCard key={i} />)}
        </div>
      ) : filteredSensors.length === 0 ? (
        <EmptyState icon={SignalIcon} title="No sensors found" description={searchTerm ? 'Try adjusting your search or filter.' : 'Get started by registering a new sensor.'} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredSensors.map((sensor: any) => {
            const config = statusConfig[sensor.status as SensorStatus];
            const StatusIcon = config.icon;
            return (
              <div key={sensor.id} className="glass-card p-4 glow-border">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={clsx('p-2 rounded-lg', config.bgColor, config.color)}><StatusIcon className="h-5 w-5" /></div>
                    <div>
                      <h3 className="text-base font-semibold text-white">{sensor.name}</h3>
                      <span className="text-xs text-gray-500">{sensorModelLabel(sensor)}</span>
                    </div>
                  </div>
                  <span className={clsx('text-[10px] px-2 py-1 rounded-full font-semibold', config.bgColor, config.color)}>{config.label}</span>
                </div>
                <div className="space-y-2.5">
                  <div className="flex items-start gap-2 text-sm">
                    <MapPinIcon className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                    <div className="text-gray-400">
                      <div>Lat: {sensor.latitude?.toFixed(4)}, Lon: {sensor.longitude?.toFixed(4)}</div>
                      {sensor.altitudeMsl && <div className="text-xs text-gray-500">Alt: {sensor.altitudeMsl}m MSL</div>}
                    </div>
                  </div>
                  {sensor.lastSeen && (
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <ClockIcon className="h-4 w-4" /><span>Last seen: {getTimeSinceLastSeen(sensor.lastSeen)}</span>
                    </div>
                  )}
                  {sensor.configuration && (
                    <div className="pt-2.5 border-t border-navy-700/50">
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        {sensor.configuration.updateRateHz && (<div><span className="text-gray-500">Update Rate</span><div className="font-medium text-white">{sensor.configuration.updateRateHz} Hz</div></div>)}
                        {sensor.configuration.maxRange && (<div><span className="text-gray-500">Max Range</span><div className="font-medium text-white">{(sensor.configuration.maxRange / 1000).toFixed(1)} km</div></div>)}
                      </div>
                    </div>
                  )}
                </div>
                <div className="mt-4 pt-3 border-t border-navy-700/50 flex gap-2">
                  <button onClick={() => navigate(`/sensors/${sensor.id}`)} className="flex-1 btn-secondary text-xs py-1.5">Details</button>
                  <button onClick={() => navigate(`/sensors/${sensor.id}`)} className="flex-1 btn-primary text-xs py-1.5">Configure</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-6 grid grid-cols-2 sm:grid-cols-5 gap-4">
        {[
          { label: 'Total', value: sensors.length, color: 'text-white' },
          { label: 'Online', value: sensors.filter((s: any) => s.status === 'ONLINE').length, color: 'text-accent' },
          { label: 'Offline', value: sensors.filter((s: any) => s.status === 'OFFLINE').length, color: 'text-gray-400' },
          { label: 'Maintenance', value: sensors.filter((s: any) => s.status === 'MAINTENANCE').length, color: 'text-yellow-400' },
          { label: 'Errors', value: sensors.filter((s: any) => s.status === 'ERROR').length, color: 'text-alert' },
        ].map((s) => (
          <div key={s.label} className="glass-card p-4">
            <p className="text-xs text-gray-500">{s.label}</p>
            <p className={`text-2xl font-display font-bold ${s.color} mt-1`}>{s.value}</p>
          </div>
        ))}
      </div>

      <RegisterSensorModal isOpen={showRegister} onClose={() => setShowRegister(false)} />
      <CsvImportModal isOpen={showCsvImport} onClose={() => setShowCsvImport(false)} />
    </div>
  );
}
